import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@emotion/react';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { api as apiAxios } from '../../api';
import { usePoll } from '../../hooks/usePoll';

// ---------------------------------------------------------------------------
// Dashboard headline stats, as a single scrolling news-style ticker.
//
// This used to be eight large cards (4 headline + 4 Generated Fees) that took
// most of the first screen. The same eight figures now scroll in one slim
// strip. Every value is measured server-side; anything not known yet renders
// "—", never a confident $0.
//
//   Total Volume Accumulated -> trailing 1h Hootdex volume  (platform-stats volume1h)
//   Volume (24H)             -> trailing 24h Hootdex volume (platform-stats totalVolume)
//   Order Book Depth (OBD)   -> live bids + asks             (platform-stats orderBookDepth)
//   HD Vault Liquidity       -> treasury x PECU price        (platform-stats hdvl)
//   Fees 24H / 7D / 30D / 365D                              (/hootdex/generatedRevenue)
//
// Both volume figures come from settled Hootdex fills (blockTransaction rows
// with origin 'HootDex', BUY/SELL) - the same trades listed further down the
// page. They previously read a collection fills are never written to, which
// is why they sat at $0.
// ---------------------------------------------------------------------------

const FEE_WINDOWS = [
  { key: 'day24h', label: 'Fees 24H', timeRange: '24h' },
  { key: 'day7', label: 'Fees 7D', timeRange: '7d' },
  { key: 'day30', label: 'Fees 30D', timeRange: '1m' },
  { key: 'day365', label: 'Fees 365D', timeRange: '1y' }
];

const isNum = (v) =>
  v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));

// "$763.36M" style - the full "$763,361,678" doesn't fit a ticker.
const formatCompactUSD = (value) => {
  if (!isNum(value)) return '—';
  const num = Number(value);
  const abs = Math.abs(num);
  if (abs >= 1e12) return '$' + (num / 1e12).toFixed(2) + 'T';
  if (abs >= 1e9) return '$' + (num / 1e9).toFixed(2) + 'B';
  if (abs >= 1e6) return '$' + (num / 1e6).toFixed(2) + 'M';
  if (abs >= 1e3) return '$' + (num / 1e3).toFixed(2) + 'K';
  return '$' + num.toFixed(2);
};

const formatFullUSD = (value) =>
  isNum(value)
    ? '$' +
      Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 })
    : 'not available yet';

const scroll = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.35; }
`;

// Change badge. Renders nothing without a real baseline (the API returns
// null until ~24h of snapshots exist) - "0.00%" would claim "flat".
function Change({ percent }) {
  if (!isNum(percent)) return null;
  const num = Number(percent);
  const up = num >= 0;
  return (
    <Box
      component="span"
      className={up ? 'green' : 'red'}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: '12px',
        fontWeight: 500,
        ml: '2px'
      }}
    >
      {up ? (
        <ArrowDropUpIcon sx={{ fontSize: 18 }} />
      ) : (
        <ArrowDropDownIcon sx={{ fontSize: 18 }} />
      )}
      {Math.abs(num).toFixed(2)}%
    </Box>
  );
}

function TickerItem({ item }) {
  return (
    <Box
      component="span"
      title={`${item.hint}: ${formatFullUSD(item.value)}`}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        px: '22px',
        whiteSpace: 'nowrap',
        borderRight: '1px solid #142420'
      }}
    >
      <Box
        component="span"
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: item.color,
          flexShrink: 0
        }}
      />
      <Box
        component="span"
        className="grey"
        sx={{
          fontSize: '11px',
          letterSpacing: '0.6px',
          textTransform: 'uppercase'
        }}
      >
        {item.label}
      </Box>
      <Box
        component="span"
        className="twhite"
        sx={{
          fontSize: '15px',
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums'
        }}
      >
        {formatCompactUSD(item.value)}
      </Box>
      <Change percent={item.changePct} />
    </Box>
  );
}

function MarketStats() {
  const [revenue, setRevenue] = useState({});
  const [platform, setPlatform] = useState(null);

  // One request per fee window; a slow 365d aggregation can't blank the
  // other three. Last good reading is kept on failure.
  const fetchRevenue = () =>
    Promise.all(
      FEE_WINDOWS.map((w) =>
        apiAxios
          .get(`/hootdex/generatedRevenue?timeRange=${w.timeRange}`)
          .then((res) => [w.key, res.data])
          .catch(() => [w.key, null])
      )
    ).then((entries) => {
      setRevenue((prev) => {
        const next = { ...prev };
        for (const [key, data] of entries) if (data) next[key] = data;
        return next;
      });
    });

  // Keep the last good payload if a poll fails, and per-field: a source that
  // reports ok:false keeps its previous value instead of dropping to "—".
  const fetchPlatformStats = () =>
    apiAxios
      .get('/wallet/platform-stats')
      .then((res) =>
        setPlatform((prev) => {
          const next = res.data || {};
          if (!prev) return next;
          const keep = (field, source) =>
            next.sources?.[source]?.ok === false ? prev[field] : next[field];
          return {
            ...next,
            totalVolume: keep('totalVolume', 'volume'),
            volume1h: keep('volume1h', 'volume'),
            orderBookDepth: keep('orderBookDepth', 'orderBookDepth'),
            hdvl: keep('hdvl', 'hdvl')
          };
        })
      )
      .catch(() => {});

  usePoll(() => fetchRevenue(), 60000, []);
  usePoll(() => fetchPlatformStats(), 30000, []);

  const items = useMemo(
    () => [
      {
        key: 'vol1h',
        label: 'Total Volume Accumulated (1H)',
        hint: 'Hootdex traded volume, last 1 hour',
        value: platform?.volume1h?.value,
        changePct: platform?.volume1h?.changePct,
        color: '#10B981'
      },
      {
        key: 'vol24h',
        label: 'Volume (24H)',
        hint: 'Hootdex traded volume, last 24 hours',
        value: platform?.totalVolume?.value,
        changePct: platform?.totalVolume?.changePct,
        color: '#10B981'
      },
      {
        key: 'obd',
        label: 'Order Book Depth (OBD)',
        hint: 'Live resting bids + asks across all markets',
        value: platform?.orderBookDepth?.value,
        changePct: platform?.orderBookDepth?.changePct,
        color: '#7C5CFC'
      },
      {
        key: 'hdvl',
        label: 'HD Vault Liquidity (HDVL)',
        hint: 'Treasury PECU x PECU price',
        value: platform?.hdvl?.value,
        changePct: platform?.hdvl?.changePct,
        color: '#4F8FF0'
      },
      ...FEE_WINDOWS.map((w) => ({
        key: w.key,
        label: w.label,
        hint: `Generated fees, ${w.label.replace('Fees ', 'last ')}`,
        value: revenue[w.key]?.generatedRevenue,
        changePct: revenue[w.key]?.changePct,
        color: '#E0B84F'
      }))
    ],
    [platform, revenue]
  );

  return (
    <Box
      role="region"
      aria-label="Hootdex market statistics"
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        width: '100%',
        height: '52px',
        background: 'rgba(4, 10, 8, 0.72)',
        border: '1px solid #142420',
        borderRadius: '12px',
        overflow: 'hidden',
        mb: 2
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          px: '16px',
          flexShrink: 0,
          background: 'rgba(16, 185, 129, 0.12)',
          borderRight: '1px solid #142420',
          color: '#10B981',
          fontSize: '12px',
          fontWeight: 700,
          letterSpacing: '1px'
        }}
      >
        <Box
          component="span"
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#10B981',
            animation: `${pulse} 1.6s ease-in-out infinite`
          }}
        />
        LIVE
      </Box>

      <Box
        sx={{
          position: 'relative',
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          // Soft fade at both edges so items glide in/out.
          maskImage:
            'linear-gradient(to right, transparent 0, #000 32px, #000 calc(100% - 32px), transparent 100%)',
          WebkitMaskImage:
            'linear-gradient(to right, transparent 0, #000 32px, #000 calc(100% - 32px), transparent 100%)',
          '&:hover .ticker-track, &:focus-within .ticker-track': {
            animationPlayState: 'paused'
          },
          '@media (prefers-reduced-motion: reduce)': {
            overflowX: 'auto',
            '& .ticker-track': { animation: 'none' },
            '& .ticker-copy': { display: 'none' }
          }
        }}
      >
        <Box
          className="ticker-track"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            height: '100%',
            width: 'max-content',
            animation: `${scroll} 45s linear infinite`
          }}
        >
          {/* Two identical copies so the loop is seamless at -50%. */}
          {items.map((item) => (
            <TickerItem key={item.key} item={item} />
          ))}
          <Box
            component="span"
            className="ticker-copy"
            aria-hidden="true"
            sx={{ display: 'inline-flex', alignItems: 'center' }}
          >
            {items.map((item) => (
              <TickerItem key={`${item.key}-copy`} item={item} />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default MarketStats;
